import { readSetting, writeSetting } from '../../infrastructure/storage/localStore';

/**
 * What this device remembers about being offered things (SPEC-016).
 *
 * Three numbers, and all three exist to keep an offer from becoming a nag: how
 * many times this browser has opened the app, when the install card was waved
 * away, and when the notification card was. The policies in `domain/pwa/` decide
 * what the numbers mean; this module only remembers them.
 *
 * Preferences, not data — nothing here is worth syncing and nothing here is
 * sensitive, which is what makes Web Storage the right home for it (CON-003).
 */

export const VISIT_COUNT_KEY = 'pwa.visits';
export const INSTALL_DISMISSED_KEY = 'pwa.installDismissedAt';
export const NOTIFICATION_DISMISSED_KEY = 'pwa.notifyDismissedAt';
/**
 * That this browser has installed the app — which is NOT the same as a dismissal
 * and cannot be held in component state.
 *
 * The card unmounts on a plant detail page and the offer is re-derived on the way
 * back, so a "hidden for now" flag in React is lost by an ordinary navigation.
 * The tab that installed the app is also still not `display-mode: standalone`,
 * so the support state falls back to the browser-menu instruction — and the card
 * returns, telling someone who has just installed the app to go and install it.
 */
export const INSTALL_COMPLETED_KEY = 'pwa.installedAt';

/**
 * This device's own "do not send" switch (SPEC-018).
 *
 * The drawer needs a way to turn notifications OFF, and a page has none:
 * `Notification.requestPermission()` is one-way and there is no counterpart to
 * revoke what it granted. So off cannot mean "permission withdrawn" — it means
 * this app stops sending, which is the only half of the arrangement we own. Both
 * the foreground watch and the service worker honour it, which is why it is
 * mirrored into Cache Storage alongside the seen list.
 */
export const NOTIFICATIONS_MUTED_KEY = 'pwa.notifyMuted';

/**
 * A visit is an app START, not a navigation.
 *
 * This app is a single page: every route change keeps the same JavaScript
 * running, so counting anything finer would turn one curious afternoon into
 * "twenty visits" and offer to install itself on the way. The flag is module
 * scope for exactly that reason — it resets when the page does, and not before.
 */
let hasCountedThisSession = false;

/**
 * A reload is the same visit, whoever asked for it.
 *
 * SPEC-010 reloads the page BY ITSELF whenever a deploy lands and the visitor is
 * backgrounded or has been idle for thirty seconds. Counting that would let the
 * app manufacture the "they came back" signal the install offer waits for — the
 * visitor never left, and the card would arrive because we shipped, not because
 * they returned. A manual reload is the same visit for the same reason.
 */
function isReload(windowObject) {
    try {
        const [navigation] = windowObject.performance.getEntriesByType('navigation');

        return Boolean(navigation) && navigation.type === 'reload';
    } catch (error) {
        // No Navigation Timing Level 2 — count it, as this always used to.
        return false;
    }
}

function readNumber(key) {
    const raw = readSetting(key);
    const parsed = Number.parseInt(raw, 10);

    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function readVisitCount() {
    return readNumber(VISIT_COUNT_KEY);
}

/** Idempotent within a page lifetime; returns the count including this visit. */
export function countVisit(windowObject = typeof window === 'undefined' ? null : window) {
    if (hasCountedThisSession) {
        return readVisitCount();
    }

    hasCountedThisSession = true;

    if (windowObject && isReload(windowObject)) {
        return readVisitCount();
    }

    const nextCount = readVisitCount() + 1;
    writeSetting(VISIT_COUNT_KEY, String(nextCount));

    return nextCount;
}

export function readInstallDismissedAt() {
    return readNumber(INSTALL_DISMISSED_KEY);
}

export function writeInstallDismissed(now = Date.now()) {
    return writeSetting(INSTALL_DISMISSED_KEY, String(now));
}

export function readInstallCompletedAt() {
    return readNumber(INSTALL_COMPLETED_KEY);
}

export function writeInstallCompleted(now = Date.now()) {
    return writeSetting(INSTALL_COMPLETED_KEY, String(now));
}

export function readNotificationsMuted() {
    return readSetting(NOTIFICATIONS_MUTED_KEY) === '1';
}

export function writeNotificationsMuted(isMuted) {
    return writeSetting(NOTIFICATIONS_MUTED_KEY, isMuted ? '1' : '0');
}

export function readNotificationDismissedAt() {
    return readNumber(NOTIFICATION_DISMISSED_KEY);
}

export function writeNotificationDismissed(now = Date.now()) {
    return writeSetting(NOTIFICATION_DISMISSED_KEY, String(now));
}

/** Test seam — the session guard would otherwise leak one spec's visit into the next. */
export function resetVisitSessionGuard() {
    hasCountedThisSession = false;
}
